/**
 * StorefrontDealsBlock
 * Banner of active automatic deals + coupon code input.
 * Renders on the public vendor storefront page.
 */
import { useState, useEffect } from "react";
import { Tag, Sparkles, Check, X, AlertCircle } from "lucide-react";
import { apiFetch } from "./api";

const styles = `
  .sdb-wrap { margin: 24px auto 32px; max-width: 920px; padding: 0 24px; display:flex; flex-direction:column; gap: 12px; }
  .sdb-deals { display:flex; flex-wrap: wrap; gap: 10px; }
  .sdb-deal {
    display:flex; align-items:center; gap:8px;
    background: linear-gradient(135deg, rgba(192,144,10,0.12), rgba(192,144,10,0.06));
    border:1px solid rgba(192,144,10,0.45);
    color: #6a4a05;
    padding: 8px 14px; border-radius: 999px;
    font-size: 12.5px; font-weight: 600;
    font-family: 'Manrope', sans-serif;
  }
  .sdb-coupon-row {
    display:flex; gap:8px; align-items:center;
    background: #faf6ee; border: 1px solid var(--border, #e4d8c8);
    border-radius: 10px; padding: 12px 14px;
  }
  .sdb-coupon-label { display:flex; align-items:center; gap:6px; font-size: 13px; font-weight:600; color:#3d2a54; }
  .sdb-coupon-input {
    flex: 1;
    border: 1px solid var(--border, #e4d8c8);
    border-radius: 8px;
    padding: 8px 12px;
    font-family: monospace;
    text-transform: uppercase;
    font-size: 13px;
    background: white;
    color: #1a1018;
  }
  .sdb-coupon-input:focus { outline: 2px solid #c0900a; }
  .sdb-btn {
    background: #2a1a3a; color: #fff; border: none;
    padding: 8px 16px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer;
    font-family: 'Manrope', sans-serif;
  }
  .sdb-btn:hover { background: #3d2a54; }
  .sdb-btn[disabled] { opacity: 0.5; cursor: not-allowed; }
  .sdb-msg { font-size: 12px; padding: 8px 12px; border-radius: 8px; display:flex; align-items:center; gap:6px; }
  .sdb-msg.ok { background: rgba(34,139,94,0.12); color: #1a5a44; border:1px solid rgba(26,90,68,0.3); }
  .sdb-msg.err { background: rgba(180,40,40,0.12); color: #8a1f1f; border:1px solid rgba(180,40,40,0.3); }
  .sdb-applied {
    background: #1a5a44; color: #fff;
    display:flex; justify-content:space-between; align-items:center;
    border-radius: 10px; padding: 10px 14px; font-size: 13px;
  }
  .sdb-clear {
    background: rgba(255,255,255,0.18); border: none; color: white;
    padding: 4px 10px; border-radius: 6px; cursor: pointer; font-size: 12px;
    display:flex; align-items:center; gap:4px;
  }
`;

function buildLabel(d) {
  switch (d.deal_type) {
    case "percent":
      return `${d.discount_value}% off`;
    case "fixed":
      return `$${d.discount_value} off`;
    case "bogo":
      return `BOGO: buy ${d.bogo_buy_qty} get ${d.bogo_get_qty} @ ${d.bogo_discount_percent}% off`;
    case "bundle":
      return `Buy ${d.bundle_min_qty}+ → ${d.discount_value}% off`;
    case "free_shipping":
      return "Free shipping";
    default:
      return d.title;
  }
}

export default function StorefrontDealsBlock({ vendorId, products }) {
  const [deals, setDeals] = useState([]);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // { kind: 'ok'|'err', text }
  const [applied, setApplied] = useState(null);

  useEffect(() => {
    if (!vendorId) return;
    apiFetch(`/marketplace/vendors/${vendorId}/deals`)
      .then(setDeals)
      .catch(() => setDeals([]));
  }, [vendorId]);

  // Build a single-cart preview using all in-stock products (just for display purposes,
  // we don't add anything to cart here). The real apply happens when the user has items
  // in their cart and uses the "Apply coupon" button.
  const applyOnCart = async () => {
    if (!code.trim() || !vendorId) return;
    setBusy(true);
    setMsg(null);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setMsg({ kind: "err", text: "Please log in to apply a code." });
        return;
      }
      const res = await fetch(`/api/cart/apply-coupon`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ vendor_id: vendorId, code: code.trim().toUpperCase() }),
      });
      const body = await res.json();
      if (!res.ok) {
        setMsg({ kind: "err", text: body.detail || "Invalid code" });
        return;
      }
      setApplied(body.applied);
      setMsg({ kind: "ok", text: `Code applied: $${(body.subtotal - body.final_subtotal).toFixed(2)} off your items from this vendor` });
    } catch (e) {
      setMsg({ kind: "err", text: e.message || "Apply failed" });
    } finally {
      setBusy(false);
    }
  };

  const clearApplied = async () => {
    const token = localStorage.getItem("token");
    if (!token) return;
    try {
      await fetch(`/api/cart/apply-coupon/${vendorId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (_e) { /* ignore */ }
    setApplied(null);
    setCode("");
    setMsg(null);
  };

  const activeDeals = deals.filter(d => d.active && (!d.expires_at || new Date(d.expires_at) > new Date()));

  if (activeDeals.length === 0 && !applied) {
    // Show coupon input only — no deals running
    return (
      <>
        <style>{styles}</style>
        <div className="sdb-wrap" data-testid="storefront-deals-block">
          <div className="sdb-coupon-row">
            <span className="sdb-coupon-label"><Tag size={14}/> Have a code?</span>
            <input
              className="sdb-coupon-input"
              value={code}
              onChange={e => setCode(e.target.value.toUpperCase())}
              placeholder="ENTER CODE"
              data-testid="storefront-coupon-input"
            />
            <button className="sdb-btn" disabled={busy || !code.trim()} onClick={applyOnCart} data-testid="storefront-apply-coupon-btn">
              {busy ? "..." : "Apply"}
            </button>
          </div>
          {msg && (
            <div className={`sdb-msg ${msg.kind}`} data-testid="storefront-coupon-msg">
              {msg.kind === "ok" ? <Check size={14}/> : <AlertCircle size={14}/>} {msg.text}
            </div>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <div className="sdb-wrap" data-testid="storefront-deals-block">
        {activeDeals.length > 0 && (
          <div className="sdb-deals" data-testid="storefront-active-deals">
            {activeDeals.map(d => (
              <div key={d.id} className="sdb-deal">
                <Sparkles size={13} /> {d.title} — {buildLabel(d)}
              </div>
            ))}
          </div>
        )}

        {applied ? (
          <div className="sdb-applied" data-testid="storefront-applied-coupon">
            <span><Check size={14}/> &nbsp;{applied.label} — ${applied.amount_off.toFixed(2)} off this vendor's items{applied.free_shipping ? " + Free Shipping" : ""}</span>
            <button className="sdb-clear" onClick={clearApplied} data-testid="storefront-clear-coupon"><X size={12}/> Remove</button>
          </div>
        ) : (
          <div className="sdb-coupon-row">
            <span className="sdb-coupon-label"><Tag size={14}/> Have a code?</span>
            <input
              className="sdb-coupon-input"
              value={code}
              onChange={e => setCode(e.target.value.toUpperCase())}
              placeholder="ENTER CODE"
              data-testid="storefront-coupon-input"
            />
            <button className="sdb-btn" disabled={busy || !code.trim()} onClick={applyOnCart} data-testid="storefront-apply-coupon-btn">
              {busy ? "..." : "Apply"}
            </button>
          </div>
        )}

        {msg && (
          <div className={`sdb-msg ${msg.kind}`} data-testid="storefront-coupon-msg">
            {msg.kind === "ok" ? <Check size={14}/> : <AlertCircle size={14}/>} {msg.text}
          </div>
        )}
      </div>
    </>
  );
}
